// src/components/ui/MessageContainer.tsx
import React from 'react';
import { createPortal } from 'react-dom';
import { MessageType } from './Message';
import Message from './Message';
import { useTranslation } from 'react-i18next';

interface MessageContainerProps {
  type: MessageType;
  message?: string | null;
  translateKey?: string | null;
  values?: Record<string, string | number>;
  isVisible: boolean;
  onClose: () => void;
  autoHide?: boolean;
  autoHideTime?: number;
  position?: 'top' | 'bottom';
}

/**
 * Container component for popup messages
 * Renders messages in a portal at the top or bottom of the screen
 */
const MessageContainer: React.FC<MessageContainerProps> = ({
  type,
  message,
  translateKey,
  values,
  isVisible,
  onClose,
  autoHide = true,
  autoHideTime = 5000,
  position = 'top'
}) => {
  useTranslation();

  // If message is not visible, don't render it
  if (!isVisible) return null;

  // Use a portal to render the message at the top DOM level
  return createPortal(
    <div 
      className={`fixed inset-x-0 z-50 px-4 ${position === 'top' ? 'top-4' : 'bottom-4'} flex justify-center`}
      aria-live="assertive"
    >
      <div className="max-w-md w-full animate-slide-down">
        <Message
          type={type}
          message={message}
          translateKey={translateKey}
          values={values}
          dismiss={onClose}
          autoHide={autoHide}
          autoHideTime={autoHideTime}
        />
      </div>
    </div>,
    document.body
  );
};

export default MessageContainer;